// Third-party
import React, { Component } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';
import chroma from 'chroma-js';
import ChartLegend from './ChartLegend';
import Nullable from '@/types/nullable';
import Dictionary from '@/types/dictionary';

type DonutDatum = {
  key?: string;
  label: string;
  value: number;
};

type Props = {
  id?: string;
  data: DonutDatum[];
} & Partial<DefaultProps>;

type DefaultProps = {
  dark?: boolean;
  size: Nullable<number>;
  animate: boolean;
  mouseTracking: boolean;
  withLegend: boolean;
  colorsDict: Nullable<Dictionary<string>>;
  legendDecimalPlaces?: number;
  innerSize?: string;
  borderWidth?: number;
  borderColor?: string;
};

type State = {
  chartOptions: Highcharts.Options;
};

class DonutChart extends Component<Props, State> {
  private defaultColorScale = chroma.scale(['#0e4263', '#0587da', '#11e9f7', '#f34190', '#f5994c', 'yellow', '#157c55']);

  static defaultProps: DefaultProps = {
    size: null,
    animate: true,
    mouseTracking: true,
    withLegend: false,
    colorsDict: null,
    legendDecimalPlaces: 2,
  };

  readonly state: State = {
    chartOptions: {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: this.props.size,
        width: this.props.size,
      },
      plotOptions: {
        pie: {
          borderWidth: this.props.borderWidth || 0,
          borderColor: this.props.borderColor || 'white',
          shadow: false,
          center: ['50%', '50%'],
          animation: {
            duration: 0,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
        },
        series: {
          enableMouseTracking: this.props.mouseTracking,
          animation: this.props.animate,
          states: {
            hover: {
              enabled: this.props.animate,
            },
          },
        },
      },
      title: undefined,
      credits: { enabled: false },
      tooltip: {
        enabled: false,
      },
    },
  };

  getDataColor = (data: DonutDatum[], index: number) => {
    const { colorsDict } = this.props;

    if (colorsDict) {
      const datum = data[index];
      if (datum.key && colorsDict[datum.key]) {
        return colorsDict[datum.key];
      }

      if (colorsDict[datum.label]) {
        return colorsDict[datum.label];
      }
    }

    return this.defaultColorScale(index / data.length).hex();
  };

  getSeriesData(data: DonutDatum[]) {
    const chartData = data.map((datum, i) => {
      const color = this.getDataColor(data, i);
      return {
        name: datum.label,
        y: datum.value,
        color,
        yDecimalPlaces: this.props.legendDecimalPlaces,
      };
    });

    return [
      {
        innerSize: this.props.innerSize || '80%',
        data: chartData,
        size: '100%',
        label: { enabled: false },
        dataLabels: {
          enabled: false,
        },
      },
    ];
  }

  updateChart() {
    const { data, size } = this.props;
    const series = this.getSeriesData(data);
    const chart = {
      type: 'pie',
      backgroundColor: 'transparent',
      height: size,
      width: size,
    };

    this.setState({ chartOptions: { series, chart } as any });
  }

  componentDidMount() {
    this.updateChart();
  }

  componentDidUpdate(prevProps: Props) {
    const { data, size } = this.props;

    if (prevProps.data !== data || prevProps.size !== size) {
      this.updateChart();
    }
  }

  render() {
    const { id, dark, withLegend } = this.props;
    const { chartOptions } = this.state;

    return (
      <div id="chart" className={classNames('donut-chart', { dark })}>
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />

        {withLegend && <ChartLegend id={id} className="donut-legend" chartOptions={chartOptions} sort={false} />}
      </div>
    );
  }
}

export default DonutChart;
